// The Event Library — single source of truth for every analytics event the app emits.
// Event names are static, snake_case, object_verb. Never construct an event name at runtime.

export const EVENTS = {
  // Foundation
  PAGE_VIEWED: "page_viewed",

  // 1. Account onboarding
  SIGN_UP_STARTED: "sign_up_started",
  EMAIL_VERIFIED: "email_verified",
  IDENTITY_VERIFICATION_STARTED: "identity_verification_started",
  IDENTITY_VERIFICATION_COMPLETED: "identity_verification_completed",
  IDENTITY_VERIFICATION_ABANDONED: "identity_verification_abandoned",
  PAYMENT_METHOD_ADDED: "payment_method_added",
  ASIC_AUTHORISATION_COMPLETED: "asic_authorisation_completed",
  ACCOUNT_CREATED: "account_created",
  SIGN_UP_ABANDONED: "sign_up_abandoned",

  // 2. Director / Nominee onboarding
  NOMINEE_SIGN_UP_STARTED: "nominee_sign_up_started",
  NOMINEE_ACCOUNT_CREATED: "nominee_account_created",

  // 3. Sign in / Password reset
  SIGN_IN_STARTED: "sign_in_started",
  SIGN_IN_IDENTIFIER_SUBMITTED: "sign_in_identifier_submitted",
  SIGN_IN_IDENTIFIER_RESOLVED: "sign_in_identifier_resolved",
  SIGN_IN_IDENTIFIER_CHANGED: "sign_in_identifier_changed",
  SIGN_IN_SUCCEEDED: "sign_in_succeeded",
  SIGN_IN_FAILED: "sign_in_failed",
  SIGN_OUT_COMPLETED: "sign_out_completed",
  SESSION_EXPIRED: "session_expired",
  PASSWORD_RESET_REQUESTED: "password_reset_requested",
  PASSWORD_RESET_COMPLETED: "password_reset_completed",

  // 3b. Enforced-invite acceptance
  INVITE_ACCEPTANCE_STARTED: "invite_acceptance_started",
  INVITE_IDENTITY_COMPLETED: "invite_identity_completed",
  INVITE_PROFILE_COMPLETED: "invite_profile_completed",
  INVITE_ACCEPTANCE_FAILED: "invite_acceptance_failed",
  INVITE_HANDOFF_VIEWED: "invite_handoff_viewed",
  INVITE_HANDOFF_CONTINUED: "invite_handoff_continued",

  // 4. Company setup
  COMPANY_ADD_STARTED: "company_add_started",
  COMPANY_ADDED: "company_added",

  // 5. Distribution creation
  DISTRIBUTION_STARTED: "distribution_started",
  DISTRIBUTION_CREATION_BLOCKED: "distribution_creation_blocked",
  DISTRIBUTION_DRAFT_SAVED: "distribution_draft_saved",
  DISTRIBUTION_DETAILS_COMPLETED: "distribution_details_completed",
  DISTRIBUTION_COMPONENTS_COMPLETED: "distribution_components_completed",
  DISTRIBUTION_PREVIEW_VIEWED: "distribution_preview_viewed",
  DISTRIBUTION_CONFIRMED: "distribution_confirmed",
  DISTRIBUTION_DRAFT_DELETED: "distribution_draft_deleted",
  DISTRIBUTION_ABA_DOWNLOADED: "distribution_aba_downloaded",
  DISTRIBUTION_STATEMENT_DOWNLOADED: "distribution_statement_downloaded",
  UNIT_ISSUE_STATEMENT_DOWNLOADED: "unit_issue_statement_downloaded",
  UNIT_TRANSFER_STATEMENT_DOWNLOADED: "unit_transfer_statement_downloaded",
  REDEMPTION_NOTICE_DOWNLOADED: "redemption_notice_downloaded",

  // 5b. Fund distribution settings (workflow #17)
  DISTRIBUTION_SETTINGS_VIEWED: "distribution_settings_viewed",
  DISTRIBUTION_SETTINGS_SAVED: "distribution_settings_saved",
  DISTRIBUTION_SETTINGS_SAVE_FAILED: "distribution_settings_save_failed",

  // 6. Share issuance / transfer / note issue / options grant (shared ReviewStep)
  SHARE_ISSUE_STARTED: "share_issue_started",
  SHARE_ISSUE_SUBMITTED: "share_issue_submitted",
  SHARE_TRANSFER_STARTED: "share_transfer_started",
  SHARE_TRANSFER_SUBMITTED: "share_transfer_submitted",
  NOTE_ISSUE_STARTED: "note_issue_started",
  NOTE_ISSUED: "note_issued",
  OPTIONS_GRANT_STARTED: "options_grant_started",
  OPTIONS_GRANTED: "options_granted",
  ASIC_FEE_ACKNOWLEDGEMENT_SHOWN: "asic_fee_acknowledgement_shown",
  ASIC_FEE_ACKNOWLEDGED: "asic_fee_acknowledged",
  ASIC_FEE_DECLINED: "asic_fee_declined",

  // 7. Approvals
  APPROVALS_QUEUE_VIEWED: "approvals_queue_viewed",
  APPROVAL_REQUEST_CREATED: "approval_request_created",
  APPROVAL_APPROVED_AND_EXECUTED: "approval_approved_and_executed",
  APPROVAL_REJECTED: "approval_rejected",
  APPROVAL_CANCELLED: "approval_cancelled",

  // 8. Fundraising
  FUNDRAISE_ROUND_STARTED: "fundraise_round_started",
  FUNDRAISE_ROUND_CREATED: "fundraise_round_created",
  FUNDRAISE_OFFER_STARTED: "fundraise_offer_started",
  FUNDRAISE_OFFER_SENT: "fundraise_offer_sent",
  FUNDRAISE_OFFER_VIEWED: "fundraise_offer_viewed",
  FUNDRAISE_OFFER_ACCEPTED: "fundraise_offer_accepted",

  // 9. Add investor / investor profile
  INVESTOR_ADD_STARTED: "investor_add_started",
  INVESTOR_DRAFT_SAVED: "investor_draft_saved",
  INVESTOR_ADDED: "investor_added",
  INVESTOR_ADD_CANCELLED: "investor_add_cancelled",

  // 10. Investor relations — announcements
  ANNOUNCEMENT_DRAFT_STARTED: "announcement_draft_started",
  ANNOUNCEMENT_PUBLISHED: "announcement_published",
  ANNOUNCEMENT_VIEWED: "announcement_viewed",

  // 11. Trading
  BUY_SHARES_STARTED: "buy_shares_started",
  BUY_SHARES_COMPLETED: "buy_shares_completed",
  BUY_SHARES_FAILED: "buy_shares_failed",
  SELL_SHARES_STARTED: "sell_shares_started",
  SELL_SHARES_COMPLETED: "sell_shares_completed",
  SELL_SHARES_FAILED: "sell_shares_failed",

  // 12. Wallet
  WITHDRAWAL_STARTED: "withdrawal_started",
  WITHDRAWAL_COMPLETED: "withdrawal_completed",
  BANK_ACCOUNT_LINKED: "bank_account_linked",
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

/** The five ReviewStep-driven funnels differentiated by the `flow` property. */
export type ReviewStepFlow = "share_issue" | "share_transfer" | "note_issue" | "options_grant" | "fundraise_offer";

/** Whether an approval-gated action executed immediately or created a pending request. */
export type Outcome = "executed" | "approval_requested" | "failed";

export type KycProvider = "averer" | "greenid" | "manual";

/** Which identity provider carried a sign-in. */
export type SignInProvider = "firebase" | "averer";

/**
 * How step 1 of the two-step sign-in form resolved, once the answer is applied.
 *
 * There is no throttled value here yet. The check the screen calls answers with a plain boolean and
 * folds a refused burst in with an ordinary negative answer, so the browser cannot tell the two
 * apart. Naming a value the screen can never send would show throttling as something that never
 * happens; separating it needs the check to report the refusal first.
 */
export type IdentifierResult = "password_available" | "password_unavailable" | "error";

/** Whether the user ended the session or it was ended for them. */
export type SignOutTrigger = "user" | "session_expired";

/**
 * The Averer failures that describe our own infrastructure rather than the person signing in.
 * These travel verbatim, so the list is data rather than a type: a caller needs to test whether a
 * reason belongs to it at runtime, and deriving the type from the list keeps the two from drifting.
 */
export const AVERER_INFRASTRUCTURE_CODES = [
  "not_configured",
  "start_failed",
  "provider_error",
  "invalid_request",
  "state_mismatch",
  "token_exchange_failed",
  "token_invalid",
  "callback_failed",
] as const;

/**
 * Averer failure detail sent to product analytics. Every account-state refusal collapses to
 * `refused` in the browser so it never leaves our systems; the specific refusal stays in our logs.
 */
export type AvererErrorCode = (typeof AVERER_INFRASTRUCTURE_CODES)[number] | "refused";

/** The three enforced-invite accept screens. */
export type InvitationType = "admin" | "investor" | "nominee";

/** Closed set of branches on which an enforced-invite acceptance can stop. No free text. */
export type InviteFailureReason =
  "identity_refused" | "profile_load_failed" | "profile_save_failed" | "handoff_start_failed";

/**
 * Which acceptance flow the person came through. The two are different funnels, not variants of one:
 * an Averer-enforced invitation proves identity on the screen, so it has an identity step, while a
 * classic invited link carries the account it belongs to and has none. Reporting them together would
 * show every classic acceptance as dropping out of a step it never had.
 */
export type InviteAcceptanceMode = "averer_enforced" | "classic";

export type FundSettingKey =
  | "allow_reinvestment"
  | "rounding_method"
  | "apca_id"
  | "tax_residency_country"
  | "investor_treatment"
  | "settlement_bsb"
  | "settlement_account_number"
  | "fi_code";

// Per-event property types — drives track()'s compile-time type-checking (FR-016).
// An event with no properties maps to `undefined` via EventProps<E> below.
export interface EventPropertyMap {
  [EVENTS.PAGE_VIEWED]: { page_name: string };

  [EVENTS.IDENTITY_VERIFICATION_STARTED]: { kyc_provider: KycProvider };
  [EVENTS.IDENTITY_VERIFICATION_COMPLETED]: { kyc_provider: KycProvider };
  [EVENTS.IDENTITY_VERIFICATION_ABANDONED]: { kyc_provider: KycProvider };
  [EVENTS.SIGN_UP_ABANDONED]: { step: string };

  [EVENTS.SIGN_IN_STARTED]: { provider?: SignInProvider };
  [EVENTS.SIGN_IN_IDENTIFIER_RESOLVED]: { result: IdentifierResult };
  // `duration_ms` is whole milliseconds since sign_in_started in the same page session. It is
  // optional because an Averer sign-in leaves the page and returns through the OIDC callback: the
  // page-session timer is gone by then, so the property is omitted rather than guessed or restarted.
  // `password_fallback` marks a password sign-in that came through the old-password link.
  [EVENTS.SIGN_IN_SUCCEEDED]: { provider?: SignInProvider; duration_ms?: number; password_fallback?: true };
  [EVENTS.SIGN_IN_FAILED]: {
    failure_reason: "invalid_credentials" | "server" | "averer_start" | "averer_callback" | "averer_required";
    provider?: SignInProvider;
    averer_error_code?: AvererErrorCode;
    duration_ms?: number;
  };
  [EVENTS.SIGN_OUT_COMPLETED]: { trigger: SignOutTrigger };

  [EVENTS.INVITE_ACCEPTANCE_STARTED]: { invitation_type: InvitationType; mode: InviteAcceptanceMode };
  [EVENTS.INVITE_IDENTITY_COMPLETED]: { invitation_type: InvitationType; mode: InviteAcceptanceMode };
  [EVENTS.INVITE_PROFILE_COMPLETED]: { invitation_type: InvitationType; mode: InviteAcceptanceMode };
  [EVENTS.INVITE_ACCEPTANCE_FAILED]: {
    invitation_type: InvitationType;
    mode: InviteAcceptanceMode;
    reason: InviteFailureReason;
  };
  [EVENTS.INVITE_HANDOFF_VIEWED]: { invitation_type: InvitationType };
  [EVENTS.INVITE_HANDOFF_CONTINUED]: { invitation_type: InvitationType; existing_averer_account: boolean };

  [EVENTS.DISTRIBUTION_PREVIEW_VIEWED]: { payee_count: number };
  [EVENTS.DISTRIBUTION_CONFIRMED]: { payee_count: number; distribution_type: string; notify_investors: boolean };

  [EVENTS.DISTRIBUTION_SETTINGS_VIEWED]: { is_configured: boolean };
  [EVENTS.DISTRIBUTION_SETTINGS_SAVED]: {
    changed_settings: FundSettingKey[];
    allow_reinvestment?: boolean;
    rounding_method?: string;
    investor_treatment?: string;
    tax_residency_country?: string;
    apca_id_changed: boolean;
    settings_complete: boolean;
  };
  [EVENTS.DISTRIBUTION_SETTINGS_SAVE_FAILED]: { failure_reason: "validation" | "server" };

  [EVENTS.SHARE_ISSUE_SUBMITTED]: { outcome: Outcome; required_approval: boolean; investor_count?: number };
  [EVENTS.SHARE_TRANSFER_SUBMITTED]: { outcome: Outcome; required_approval: boolean };
  [EVENTS.NOTE_ISSUED]: { outcome: Outcome; required_approval: boolean };
  [EVENTS.OPTIONS_GRANTED]: { outcome: Outcome; required_approval: boolean };
  [EVENTS.FUNDRAISE_OFFER_SENT]: { outcome: Outcome; investor_count?: number };

  // ConfirmAsicFeeModal is also used outside the 5 ReviewStep flows (officeholder appoint/remove,
  // note management, options exercise), so `flow` is a general category string here too.
  [EVENTS.ASIC_FEE_ACKNOWLEDGEMENT_SHOWN]: { flow: string };
  [EVENTS.ASIC_FEE_ACKNOWLEDGED]: { flow: string };
  [EVENTS.ASIC_FEE_DECLINED]: { flow: string };

  [EVENTS.APPROVALS_QUEUE_VIEWED]: { pending_count: number };
  // ReviewStep-driven requests (issue/transfer/note/options/fundraise) always use ReviewStepFlow;
  // the approver-side events below also cover other approval-gated action types (e.g. announcements),
  // so `flow` is a general category string there rather than the narrower ReviewStepFlow union.
  [EVENTS.APPROVAL_REQUEST_CREATED]: { flow: ReviewStepFlow };
  [EVENTS.APPROVAL_APPROVED_AND_EXECUTED]: { flow: string };
  [EVENTS.APPROVAL_REJECTED]: { flow: string; reason_provided: boolean };
  [EVENTS.APPROVAL_CANCELLED]: { flow: string };

  [EVENTS.INVESTOR_ADD_STARTED]: { initiated_by: "admin" | "external_investor" };
  [EVENTS.INVESTOR_ADD_CANCELLED]: { step: string };

  [EVENTS.ANNOUNCEMENT_PUBLISHED]: { outcome: Outcome; required_approval: boolean };

  [EVENTS.BUY_SHARES_FAILED]: { failure_reason: "insufficient_units" | "server" };
  [EVENTS.SELL_SHARES_FAILED]: { failure_reason: "price_changed" | "offer_declined" };

  [EVENTS.WITHDRAWAL_STARTED]: { asset_type: "funds" | "tokens" };
  [EVENTS.WITHDRAWAL_COMPLETED]: { asset_type: "funds" | "tokens" };
}

export type EventProps<E extends EventName> = E extends keyof EventPropertyMap ? EventPropertyMap[E] : undefined;
